<template>
    <div class="captain-panel">
        <div class="captain-panel__head">
            <v-icon color="orange">mdi-alpha-c-circle-outline</v-icon>
            <div>
                <div class="captain-panel__title">Νέο αίτημα</div>
                <div class="captain-panel__hint">Ως αρχηγός της ομάδας μπορείς να ζητήσεις αναβολή ή αλλαγή ώρας αγώνα</div>
            </div>
        </div>

        <div v-if="postponements" class="captain-panel__summary">
            <div class="captain-panel__count">
                <span class="captain-panel__number">{{ postponements.remaining }}</span>
                <span>/ {{ postponements.allowed }}</span>
            </div>
            <div class="captain-panel__hint">
                διαθέσιμες αναβολές για τη σεζόν · το αίτημα γίνεται έως {{ postponements.deadlineHours }} ώρες πριν τον αγώνα
                και ο αγώνας αναβάλλεται μόλις εγκριθεί από τη διοργάνωση
            </div>
        </div>

        <div v-if="matchesLoading" class="text-center py-3">
            <v-progress-circular indeterminate size="22" width="2" color="orange" />
        </div>
        <template v-else-if="upcomingMatches.length">
            <div class="captain-panel__section">Επόμενοι αγώνες</div>
            <div v-for="match in upcomingMatches" :key="match.id" class="captain-panel__match">
                <router-link :to="{ name: 'Match', params: { id: match.id } }" class="captain-panel__match-info">
                    <div class="captain-panel__teams">{{ match.team1.name }} - {{ match.team2.name }}</div>
                    <div class="captain-panel__hint">{{ formatMatchDate(match) }}</div>
                </router-link>
                <span v-if="match.postponed && !hasRequest(match)" class="captain-panel__postponed">ΑΝΑΒΟΛΗ</span>
                <div v-else class="captain-panel__actions">
                    <postponement-request
                        :match="match"
                        :postponements="postponements"
                        compact
                        @requested="onPostponementRequested"
                    />
                    <match-change-request-button :match="match" compact @requested="$emit('requested')" />
                </div>
            </div>
        </template>
        <div v-else class="captain-panel__hint mt-3">Δεν υπάρχουν προγραμματισμένοι αγώνες της ομάδας</div>
    </div>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import matchService from '@/services/match'
    import postponementService from '@/services/postponement'
    import PostponementRequest from '@/components/PostponementRequest'
    import MatchChangeRequestButton from '@/components/MatchChangeRequestButton'

    const UPCOMING_MATCHES = 5

    // For the captain of the team picked on the profile: the postponements left and the next matches of the team,
    // each with a postponement and a match change request. Emits `requested` after a new request.
    export default {
        name: 'CaptainRequestPanel',
        components: { PostponementRequest, MatchChangeRequestButton },
        data() {
            return {
                postponements: null,
                matches: [],
                matchesLoading: false
            }
        },
        computed: {
            team() {
                return this.$store.getters.getTeam
            },
            // the next matches of the team, and the postponed ones the team asked for
            upcomingMatches() {
                const now = Date.now()
                return this.matches
                    .filter(match => match.matchDatetime && momentTimezone(match.matchDatetime).valueOf() > now)
                    .filter(match => !match.postponed || this.hasRequest(match))
                    .slice(0, UPCOMING_MATCHES)
            }
        },
        created() {
            this.fetchMatches()
            this.fetchPostponements()
        },
        methods: {
            fetchMatches() {
                if (!this.team) return
                this.matchesLoading = true
                // the team matches come without the cup ones unless asked for
                Promise.all([
                    matchService.getMatchesByTeam(this.team.id).then(resp => (resp && resp.data) || []).catch(() => []),
                    matchService.getMatchesByTeam(this.team.id, null, 'cup').then(resp => (resp && resp.data) || []).catch(() => [])
                ]).then(([league, cup]) => {
                    this.matches = league.concat(cup)
                        .sort((a, b) => momentTimezone(a.matchDatetime).valueOf() - momentTimezone(b.matchDatetime).valueOf())
                }).finally(() => {
                    this.matchesLoading = false
                })
            },
            fetchPostponements() {
                if (!this.team) return
                postponementService.getTeamPostponements(this.team.id).then(resp => {
                    this.postponements = resp.data
                }).catch(error => console.error('Could not load the postponements of the team', error))
            },
            onPostponementRequested(request) {
                this.fetchPostponements()
                if (request) {
                    this.$emit('requested')
                }
            },
            hasRequest(match) {
                return !!this.postponements && (this.postponements.requests || []).some(request => request.matchId === match.id)
            },
            formatMatchDate(match) {
                return momentTimezone(match.matchDatetime).tz('Europe/Athens').locale('el').format('ddd D MMM · HH:mm')
            }
        }
    }
</script>

<style scoped>
.captain-panel {
    padding: 16px 18px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    text-align: left;
}

.captain-panel__head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding-bottom: 14px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.captain-panel__title {
    font-weight: 800;
    font-size: 1.05rem;
}

.captain-panel__hint {
    font-size: 0.82rem;
    color: rgba(0, 0, 0, 0.6);
}

.captain-panel__summary {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: 14px;
}

.captain-panel__count {
    flex: 0 0 auto;
    font-weight: 800;
    color: rgba(0, 0, 0, 0.6);
}

.captain-panel__number {
    font-size: 2rem;
    line-height: 1;
    color: #ef6c00;
}

.captain-panel__section {
    margin: 18px 0 6px;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.6);
}

.captain-panel__match {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px 12px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.captain-panel__match:last-child {
    border-bottom: 0;
}

.captain-panel__match-info {
    flex: 1 1 180px;
    min-width: 0;
    color: inherit !important;
    text-decoration: none;
}

.captain-panel__teams {
    font-weight: 700;
    word-break: break-word;
}

.captain-panel__actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
}

.captain-panel__postponed {
    font-size: 0.75rem;
    font-weight: 800;
    color: #c62828;
}
</style>
